import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { SlidersHorizontal, X } from 'lucide-react';
import ProductCard from '@/components/ProductCard';
import { ProductGridSkeleton } from '@/components/ProductCardSkeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { CATEGORIES } from '@/mock/products';
import { useAvailableProducts, useCategories } from '@/store/dataStore';
import { useSEO } from '@/hooks/useSEO';

const PAGE_SIZE = 24;

export default function CatalogPage() {
  const { products: PRODUCTS, loading: productsLoading } = useAvailableProducts();
  const { categories } = useCategories();
  const CATS = categories && categories.length ? categories : CATEGORIES;
  const [searchParams, setSearchParams] = useSearchParams();
  const cat = searchParams.get('cat') || '';
  const q = searchParams.get('q') || '';
  const col = searchParams.get('col') || '';
  const [sort, setSort] = useState('featured');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const sentinelRef = useRef(null);

  useEffect(() => { window.scrollTo(0, 0); setVisibleCount(PAGE_SIZE); }, [cat, q, col, sort, minPrice, maxPrice]);

  const filtered = useMemo(() => {
    let list = [...PRODUCTS];
    if (cat) {
      const norm = (v) => String(v || '').normalize('NFD').replace(/[\u0300-\u036f]/g, '').trim().toLowerCase();
      const c = CATS.find((x) => x.id === cat);
      const keys = [norm(cat), norm(c?.name)];
      list = list.filter((p) => keys.includes(norm(p.category)));
    }
    if (col) list = list.filter((p) => (p.collection || '').trim() === col);
    if (q) {
      const term = q.toLowerCase();
      list = list.filter((p) =>
        p.name.toLowerCase().includes(term) ||
        (p.barcode || '').toLowerCase() === term ||
        (p.internal_code || '').toLowerCase() === term ||
        (p.collection || '').toLowerCase().includes(term)
      );
    }
    if (minPrice) list = list.filter((p) => p.price >= parseFloat(minPrice));
    if (maxPrice) list = list.filter((p) => p.price <= parseFloat(maxPrice));

    if (sort === 'price-asc') list.sort((a, b) => a.price - b.price);
    else if (sort === 'price-desc') list.sort((a, b) => b.price - a.price);
    else if (sort === 'name') list.sort((a, b) => a.name.localeCompare(b.name));
    else list.sort((a, b) => (b.isFeatured ? 1 : 0) - (a.isFeatured ? 1 : 0));
    return list;
  }, [cat, col, q, sort, minPrice, maxPrice, PRODUCTS, CATS]);

  const currentCat = CATS.find((c) => c.id === cat);
  const visible = filtered.slice(0, visibleCount);
  const hasMore = visibleCount < filtered.length;

  useSEO({
    title: col ? `Colección ${col} · BIGTIME` : currentCat ? `${currentCat.name} al mayor · BIGTIME Venezuela` : q ? `Búsqueda: ${q} · BIGTIME` : 'Catálogo completo · BIGTIME Venezuela',
    description: currentCat ? `${currentCat.name} al mayor desde 1 pieza. Precios mayoristas para tiendas y revendedores en Venezuela.` : 'Catálogo completo de BIGTIME: relojería, belleza, accesorios, electrónicos y más al mayor desde 1 pieza.',
    keywords: `${currentCat?.name || 'catálogo'} al mayor, mayorista Venezuela, BIGTIME`,
  });

  // Derive the list of available collections from the current catalog. We
  // build it BEFORE applying the `col` filter so switching a filter still
  // exposes the same options to the user.
  const collections = useMemo(() => {
    const set = new Map(); // name -> count
    for (const p of PRODUCTS) {
      const name = (p.collection || '').trim();
      if (!name) continue;
      set.set(name, (set.get(name) || 0) + 1);
    }
    return Array.from(set.entries()).sort((a, b) => a[0].localeCompare(b[0]));
  }, [PRODUCTS]);

  // Infinite scroll: as soon as the sentinel div enters the viewport, bump
  // the visible page. Uses IntersectionObserver for a single global handler
  // that scales to millions of catalog items without blocking scroll.
  useEffect(() => {
    if (!hasMore || !sentinelRef.current) return;
    const io = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        setVisibleCount((n) => Math.min(n + PAGE_SIZE, filtered.length));
      }
    }, { rootMargin: '600px 0px' });
    io.observe(sentinelRef.current);
    return () => io.disconnect();
  }, [hasMore, filtered.length]);

  return (
    <div className="max-w-7xl mx-auto px-3 md:px-4 py-4 md:py-6">
      <div className="text-sm text-slate-500 mb-2">
        <Link to="/" className="hover:text-[#1B2A4A]">Inicio</Link> / <span className="text-[#1B2A4A] font-medium">{currentCat ? currentCat.name : q ? `Búsqueda: ${q}` : 'Todos los productos'}</span>
      </div>
      <h1 className="text-2xl md:text-4xl font-extrabold text-[#1B2A4A] mb-4 md:mb-6">
        {col ? `Colección: ${col}` : currentCat ? currentCat.name : q ? `Resultados para "${q}"` : 'Catálogo completo'}
      </h1>

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Sidebar filters */}
        <aside className={`lg:w-64 flex-shrink-0 ${showFilters ? 'fixed inset-0 z-40 bg-white overflow-y-auto p-4 lg:relative lg:p-0 lg:bg-transparent' : 'hidden lg:block'}`}>
          <div className="flex items-center justify-between mb-3 lg:hidden">
            <h3 className="font-bold text-[#1B2A4A]">Filtros</h3>
            <button onClick={() => setShowFilters(false)}><X size={20} /></button>
          </div>
          <div className="bg-white border border-slate-200 rounded-xl p-4 sticky top-24">
            <h4 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Categorías</h4>
            <ul className="space-y-1 mb-5">
              <li>
                <button onClick={() => setSearchParams(q ? { q } : {})} className={`w-full text-left text-sm py-1.5 px-2 rounded ${!cat ? 'bg-yellow-100 text-[#1B2A4A] font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}>Todas</button>
              </li>
              {CATS.map((c) => (
                <li key={c.id}>
                  <button
                    onClick={() => setSearchParams(q ? { cat: c.id, q } : { cat: c.id })}
                    className={`w-full text-left text-sm py-1.5 px-2 rounded ${cat === c.id ? 'bg-yellow-100 text-[#1B2A4A] font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}
                  >
                    {c.name}
                  </button>
                </li>
              ))}
            </ul>

            <h4 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Precio (USD)</h4>
            <div className="flex gap-2 mb-4">
              <Input type="number" placeholder="Min" value={minPrice} onChange={(e) => setMinPrice(e.target.value)} className="h-9" />
              <Input type="number" placeholder="Max" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} className="h-9" />
            </div>

            {collections.length > 0 && (
              <>
                <h4 className="text-xs uppercase tracking-wider font-bold text-slate-500 mb-3">Colecciones</h4>
                <ul className="space-y-1 mb-4 max-h-56 overflow-y-auto" data-testid="catalog-collections-list">
                  <li>
                    <button
                      onClick={() => {
                        const next = new URLSearchParams(searchParams);
                        next.delete('col');
                        setSearchParams(next);
                      }}
                      className={`w-full text-left text-sm py-1.5 px-2 rounded ${!col ? 'bg-yellow-100 text-[#1B2A4A] font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}
                      data-testid="catalog-col-all"
                    >Todas</button>
                  </li>
                  {collections.map(([name, count]) => (
                    <li key={name}>
                      <button
                        onClick={() => {
                          const next = new URLSearchParams(searchParams);
                          next.set('col', name);
                          setSearchParams(next);
                        }}
                        className={`w-full text-left text-sm py-1.5 px-2 rounded flex items-center justify-between ${col === name ? 'bg-yellow-100 text-[#1B2A4A] font-semibold' : 'hover:bg-slate-50 text-slate-700'}`}
                        data-testid={`catalog-col-${name}`}
                      >
                        <span className="truncate">{name}</span>
                        <span className="text-[10px] text-slate-400">{count}</span>
                      </button>
                    </li>
                  ))}
                </ul>
              </>
            )}

            <Button onClick={() => { setMinPrice(''); setMaxPrice(''); setSearchParams({}); }} variant="outline" className="w-full h-9 text-sm border-[#1B2A4A] text-[#1B2A4A] hover:bg-[#1B2A4A] hover:text-yellow-300">Limpiar filtros</Button>
          </div>
        </aside>

        {/* Products */}
        <div className="flex-1">
          <div className="flex items-center justify-between mb-4 gap-3">
            <p className="text-sm text-slate-600 shrink-0">{filtered.length} producto{filtered.length !== 1 ? 's' : ''}</p>
            <div className="flex items-center gap-2 min-w-0">
              <button onClick={() => setShowFilters(true)} data-testid="catalog-filters-btn" className="lg:hidden shrink-0 inline-flex items-center gap-1.5 text-sm border border-slate-300 rounded-full px-3 h-10">
                <SlidersHorizontal size={14} /> Filtros
              </button>
              <select value={sort} onChange={(e) => setSort(e.target.value)} data-testid="catalog-sort" className="min-w-0 max-w-[150px] sm:max-w-none text-sm border border-slate-300 rounded-full px-3 h-10 bg-white">
                <option value="featured">Destacados</option>
                <option value="price-asc">Precio: menor a mayor</option>
                <option value="price-desc">Precio: mayor a menor</option>
                <option value="name">Nombre A-Z</option>
              </select>
            </div>
          </div>

          {productsLoading && PRODUCTS.length === 0 ? (
            // First-load with no local cache: give the grid a silhouette so
            // the layout doesn't shift when the real products land.
            <ProductGridSkeleton count={8} />
          ) : filtered.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl p-12 text-center">
              <p className="text-slate-500">No se encontraron productos con esos filtros.</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 gap-2.5 md:gap-4" data-testid="catalog-grid">
                {visible.map((p) => <ProductCard key={p.id} product={p} />)}
              </div>
              {hasMore && (
                <div ref={sentinelRef} className="mt-8 text-center py-6" data-testid="catalog-sentinel">
                  <div className="inline-block w-8 h-8 border-3 border-yellow-300 border-t-[#1B2A4A] rounded-full animate-spin" />
                  <p className="text-xs text-slate-500 mt-2">Cargando más productos… ({visible.length} de {filtered.length})</p>
                </div>
              )}
              {!hasMore && filtered.length > PAGE_SIZE && (
                <p className="text-center text-xs text-slate-400 mt-8 py-4" data-testid="catalog-end">
                  Has visto los {filtered.length} productos disponibles ✨
                </p>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
